import * as _ from 'lodash-es';
import type { ComponentProps } from 'react';
import React, { useMemo, useState, useTransition } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import type { QueryResult } from '@apollo/client';
import { ApolloError, useLazyQuery, useQuery } from '@apollo/client';
import PageLayout from '../app/layout/PageLayout';
import TwoColumnRow from '../app/layout/TwoColumnRow';
import useAppSelector from '../app/redux/useAppSelector';
import LoadingIndicator from '../components/LoadingIndicator';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import type { SearchViewExternalResultsQuery } from '../graphql/generated/graphql';
import { SEARCH_PLATFORM_INFO_MAP } from '../integrations/IntegrationPlatformInfo';
import useIsLayoutSize from '../post/utils/useIsLayoutSize';
import { LayoutSize } from '../post/utils/useLayoutSize';
import { parseSearchQuery } from '../topbar/quillFacets';
import { isElectron } from '../utils/desktopClient';
import SearchManagePlatformButton from './SearchManagePlatformButton';
import * as styles from './SearchView.module.scss';
import SearchViewExternalResults from './SearchViewExternalResults';
import SearchViewFilterMenu from './SearchViewFilterMenu';
import SearchViewHeader from './SearchViewHeader';
import SearchViewSlabSearch from './SearchViewSlabSearch';
import getSearchResultType, {
  filterBySearchResultType,
} from './getSearchResultType';

const ENCOURAGE_INSTALLATION_PLATFORMS = ['google-drive', 'slack'];

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const SLAB_SEARCH_TYPES_QUERY = gql(/** @complexity 2 */ `
  query SearchViewSlabSearchTypes(
    $query: String!
    $filter: SearchFilter
  ) {
    searchTypes(
      query: $query
      filter: $filter
    ) {
      type
      count
    }
  }
`);

const EXTERNAL_SEARCH_QUERY = gql(/** @complexity 68 */ `
  query SearchViewExternalResults($platform: String!, $query: String!) {
    externalSearch(platform: $platform, query: $query) {
      id
      ...SearchViewExternalResults_mention
    }
  }
`);

const USE_EXTERNAL_SEARCH_QUERY_PLATFORM_FRAGMENT = gql(`
  fragment useExternalSearchQuery_platform on ExternalSearchPlatform {
    platform
    personalAuthorized
    teamAuthorized
  }
`);

const useExternalSearchQuery = ({
  searchQuery,
  platform,
  type,
  skip,
}: {
  searchQuery: string;
  platform?: DocumentType<typeof USE_EXTERNAL_SEARCH_QUERY_PLATFORM_FRAGMENT>;
  type?: string;
  skip: boolean;
}) => {
  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
    personalAuthorized,
    platform: platformKey = '',
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
    teamAuthorized,
  } = platform ?? {};

  const [externalResults, setExternalResults] = useState<
    Record<
      string,
      Pick<
        QueryResult<
          SearchViewExternalResultsQuery,
          { platform: string; query: string }
        >,
        'data' | 'error'
      >
    >
  >({});

  const platformInfo = SEARCH_PLATFORM_INFO_MAP[platformKey];
  const isAuthorized = personalAuthorized || teamAuthorized;

  const [loadExternalSearch] = useLazyQuery(EXTERNAL_SEARCH_QUERY);

  const loadPlatformData = async (
    query: string,
    externalPlatformKey: string,
  ) => {
    const res = await loadExternalSearch({
      variables: { query, platform: externalPlatformKey },
      fetchPolicy: 'network-only',
    });

    // This will work around the issue where the error field is not set in the lazy query executor result
    // https://github.com/apollographql/apollo-client/issues/12097#issuecomment-2523542147
    const error =
      res.error ??
      (res.errors ? new ApolloError({ graphQLErrors: res.errors }) : undefined);

    setExternalResults((d) => ({
      ...d,
      [externalPlatformKey]: { data: res.data, error },
    }));
    return { count: res.data?.externalSearch?.length ?? null };
  };

  const [previousSearchQuery, setPreviousSearchQuery] = useState(searchQuery);
  if (searchQuery !== previousSearchQuery) {
    setPreviousSearchQuery(searchQuery);
    setExternalResults({});
  }

  const { data, error } = externalResults[platformKey] ?? {};

  const results =
    platformInfo && isAuthorized ? (data?.externalSearch ?? []) : [];
  const filteredResults = _.compact(filterBySearchResultType(type, results));

  return {
    results,
    filteredResults,
    error,
    loading: !!platformKey && !externalResults[platformKey] && !skip,
    loadPlatformData,
  };
};

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const EXTERNAL_PLATFORMS_QUERY = gql(/** @complexity 2 */ `
  query SearchViewExternalPlatforms($redirectPath: String!) {
    externalSearchPlatforms(redirectPath: $redirectPath) {
      ...useExternalSearchQuery_platform
      ...SearchManagePlatformButton_platforms
      ...SearchViewHeader_platforms
      ...SearchViewExternalResults_platform
    }
  }
`);

const useExternalSearchPlatforms = () => {
  const userType = useAppSelector((state) => state.session.user.type);

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const q = searchParams.get('q');

  const params = new URLSearchParams({
    platform: '$PLATFORM',
  });

  if (q != null) {
    params.set('q', q);
  }

  const redirectPath = `${location.pathname}?${params.toString()}`;

  const { data, loading, refetch } = useQuery(EXTERNAL_PLATFORMS_QUERY, {
    fetchPolicy: 'network-only',
    variables: { redirectPath },
  });

  const externalSearchPlatforms = data?.externalSearchPlatforms;

  return useMemo(() => {
    const results = externalSearchPlatforms?.map((platform) => ({
      ...platform,
      teamAuthorized: userType !== 'GUEST' ? platform.teamAuthorized : false,
    }));

    return { loading, data: results, refetch };
  }, [externalSearchPlatforms, loading, refetch, userType]);
};

const useEncourageInstallationSet = (
  platforms?: { personalAuthorized?: boolean; teamAuthorized?: boolean }[],
) => {
  const dismissedSearchConnectionPrompts = useAppSelector(
    (state) => state.ui.dismissedSearchConnectionPrompts,
  );

  return useMemo(() => {
    if (
      isElectron ||
      !platforms ||
      platforms.some((p) => p.personalAuthorized || p.teamAuthorized)
    ) {
      return new Set([]);
    }

    return new Set(
      _.without(
        ENCOURAGE_INSTALLATION_PLATFORMS,
        ...dismissedSearchConnectionPrompts,
      ),
    );
  }, [dismissedSearchConnectionPrompts, platforms]);
};

interface SearchViewProps {
  initialPlatform?: string;
  initialType?: string;
}

const SearchView = ({ initialPlatform, initialType }: SearchViewProps) => {
  const [, startTransition] = useTransition();
  const [tab, setTab] = useState({
    platform: initialPlatform,
    type: initialType,
  });

  const setPlatform = (key?: string) => {
    if (key === tab.platform) {
      return;
    }

    startTransition(() => {
      setTab({ platform: key, type: undefined });
    });
  };

  const setType = (key?: string) => {
    if (key === tab.type) {
      return;
    }

    startTransition(() => {
      setTab((currentTab) => ({ platform: currentTab.platform, type: key }));
    });
  };

  const [searchParams] = useSearchParams();
  const searchInput = searchParams.get('q') ?? '';
  const authError = searchParams.get('authError') ?? '';

  const platformKey =
    tab.platform && SEARCH_PLATFORM_INFO_MAP[tab.platform]?.platform;

  const parsedVariables = useMemo(
    () => parseSearchQuery(searchInput),
    [searchInput],
  );

  const { skip, variables } = useMemo(() => {
    const { query } = parsedVariables;

    return {
      skip: !query && Object.keys(parsedVariables.filter ?? {}).length === 0,
      variables: { ...parsedVariables, query },
    };
  }, [parsedVariables]);

  const { data: searchTypesData, loading: isSearchTypesQueryLoading } =
    useQuery(SLAB_SEARCH_TYPES_QUERY, {
      variables,
      skip,
    });
  const searchTypes = searchTypesData?.searchTypes ?? null;

  const { data: platforms, refetch: refetchPlatforms } =
    useExternalSearchPlatforms();
  const platform = platforms?.find((p) => p.platform === platformKey);
  const encouragedPlatformSet = useEncourageInstallationSet(platforms);

  const externalResponse = useExternalSearchQuery({
    searchQuery: variables.query,
    platform,
    type: tab.type,
    skip,
  });

  const externalCountsByType = _.countBy(
    externalResponse.results,
    getSearchResultType,
  );

  const countsByType: React.ComponentProps<
    typeof SearchViewHeader
  >['countsByType'] =
    searchTypes?.reduce((acc, searchType) => {
      return {
        ...acc,
        [searchType.type.toLowerCase()]: searchType.count,
      };
    }, {}) ?? {};

  // In case we are on a tab that has no results,
  // we need to reset the tab, so that we can show all results instead
  const shouldResetTab =
    !isSearchTypesQueryLoading &&
    platformKey == null &&
    tab.type != null &&
    countsByType[tab.type] == null;

  if (shouldResetTab) {
    setTab({ platform: undefined, type: undefined });
  }

  const headerProps = {
    activePlatformKey: platformKey,
    encouragedPlatformSet,
    platforms: platforms || [],
    type: tab.type,
    setType,
    setPlatform,
    externalCountsByType,
    countsByType,
    query: variables.query,
    skip,
    loadPlatformData: externalResponse.loadPlatformData,
  } as const satisfies Partial<ComponentProps<typeof SearchViewHeader>>;

  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  const managePlatformButton =
    isElectron || isMobile ? null : (
      <SearchManagePlatformButton
        platforms={platforms || []}
        refetch={refetchPlatforms}
      />
    );

  const shouldShowResultsFilter =
    !platform ||
    !encouragedPlatformSet.has(platform.platform) ||
    !!externalResponse.filteredResults.length;

  return (
    <PageLayout.Content noPaddingX noPaddingY>
      <div className={styles.container}>
        <TwoColumnRow
          main={(isAsideShown) => (
            <>
              <div className={styles.headerContainer}>
                <div className={styles.headerTitleContainer}>
                  <div className={styles.headerTitle}>
                    <h2>Search results</h2>
                    {!skip && (
                      <div className={styles.loading}>
                        <LoadingIndicator delay={0} adaptColor />
                      </div>
                    )}
                  </div>
                  {isAsideShown
                    ? shouldShowResultsFilter && <SearchViewFilterMenu />
                    : managePlatformButton}
                </div>
              </div>
              {!isAsideShown && (
                <SearchViewHeader {...headerProps} vertical={false} />
              )}
              <div className={styles.results}>
                {platform && (
                  <SearchViewExternalResults
                    authError={authError}
                    encouragedPlatformSet={encouragedPlatformSet}
                    platform={platform}
                    results={externalResponse.filteredResults}
                    loading={externalResponse.loading}
                    error={externalResponse.error}
                  />
                )}
                {!skip && (
                  <>
                    <SearchViewSlabSearch
                      variables={variables}
                      active={!tab.platform && tab.type == null}
                    />
                    {Object.keys(countsByType).map((type) => (
                      <SearchViewSlabSearch
                        key={type}
                        type={type}
                        variables={variables}
                        active={!tab.platform && type === tab.type}
                      />
                    ))}
                  </>
                )}
              </div>
            </>
          )}
          aside={
            <SearchViewHeader
              {...headerProps}
              vertical
              verticalExtra={
                <div className={styles.verticalExtraContainer}>
                  {managePlatformButton}
                </div>
              }
            />
          }
        />
      </div>
    </PageLayout.Content>
  );
};

export default SearchView;
